import {_utils, viewportChecker} from "../../utils/_utils.js";
import {_libs} from "../../utils/_libs.js";
import {_state} from "../../state/state.js";
import {gsap} from "gsap";

/**
 * "Every Journey, Seamlessly Orchestrated" (TZ, section 2) — DESKTOP ONLY.
 * 1. The section slides over the hero — the hero stays pinned (pinSpacing: false) until it's fully covered.
 * 2. The section pins, the split title (left words on the left, right words on the right, 70px)
 *    merges to the center (90px), then the emblem fades in (Figma "Shape Container": opacity 0 → 0.1).
 *    Scrubbed, so scrolling back plays it in reverse.
 * Mobile/tablet: no pins, no transforms — static layout from CSS (timelines killed + props cleared on rebuild).
 */
const START_SCALE = 70 / 90; // UI-kit H2 (start) → 90px (merged)
const LEFT_EDGE = 0.205; // start: left words end at ~20.5% of the width (Figma 938:3767)
const RIGHT_EDGE = 0.748; // start: right words begin at ~74.8%

export const hpJourney = () => {
    return new Promise((resolve) => {
        _utils.initSection('.hp-journey', (section) => init(section));
        resolve();
    });
};

const init = (section) => {
    const hero = section.previousElementSibling?.classList.contains('hp-hero') ? section.previousElementSibling : null;
    const leftWords = section.querySelectorAll('.hp-journey__word--left');
    const rightWords = section.querySelectorAll('.hp-journey__word--right');
    const emblem = section.querySelector('.hp-journey__emblem');
    const animated = [...leftWords, ...rightWords, emblem].filter(Boolean);

    let heroTl = null;
    let tl = null;

    // Offsets are measured on the merged (CSS) layout; function-based values are re-evaluated on refresh.
    const offsetLeft = (word) => {
        const rect = word.getBoundingClientRect();
        const current = gsap.getProperty(word, 'x') || 0;
        // right edge doesn't move when scaling from the right (transform-origin: right)
        return section.clientWidth * LEFT_EDGE - (rect.right - current);
    };
    const offsetRight = (word) => {
        const rect = word.getBoundingClientRect();
        const current = gsap.getProperty(word, 'x') || 0;
        // left edge doesn't move when scaling from the left (transform-origin: left)
        return section.clientWidth * RIGHT_EDGE - (rect.left - current);
    };

    const createTl = () => {
        [heroTl, tl].forEach((timeline) => {
            if (timeline !== null) {
                timeline.scrollTrigger && timeline.scrollTrigger.kill(true);
                timeline.kill();
            }
        });
        heroTl = null;
        tl = null;
        if (animated.length) gsap.set(animated, {clearProps: 'transform,opacity'});

        if (!_state.isDesktop) return;

        // 1. hero stays in place while this section slides over it
        if (hero) {
            heroTl = _libs._gsap({
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                pin: true,
                pinSpacing: false,
                //markers: true,
            });
        }

        // 2. pinned title merge
        if (!leftWords.length && !rightWords.length) return;

        tl = _libs._gsap({
            trigger: section,
            start: 'top top',
            end: '+=100%',
            pin: true,
            scrub: true,
            invalidateOnRefresh: true,
            //markers: true,
        });

        tl.fromTo(leftWords,
            {x: (i, el) => offsetLeft(el), scale: START_SCALE},
            {x: 0, scale: 1, ease: 'power2.inOut', duration: 1},
            0
        );
        tl.fromTo(rightWords,
            {x: (i, el) => offsetRight(el), scale: START_SCALE},
            {x: 0, scale: 1, ease: 'power2.inOut', duration: 1},
            0
        );

        if (emblem) {
            tl.fromTo(emblem, {opacity: 0}, {opacity: 0.1, ease: 'none', duration: 0.4}, 1);
        }
    };

    createTl();

    // Rebuild only when crossing the desktop breakpoint. Other size changes (incl. the theme's "resizePage" on
    // any page-height change) are handled by ScrollTrigger.refresh() inside _libs._gsap — re-creating pins here
    // mid-scroll would make the page jump.
    let wasDesktop = _state.isDesktop;
    _utils.rebuildComponentsOnResize(() => {
        viewportChecker.all();
        if (_state.isDesktop === wasDesktop) return;
        wasDesktop = _state.isDesktop;
        createTl();
    });
};
